import type enDomain from '@/shared/i18n/locales/en/domain.json';
import type enNav from '@/shared/i18n/locales/en/nav.json';
import type { NavCounters } from '@/shared/domain/selectors';
import type { CapabilityKey } from '@/shared/domain/identity';

/* Sidebar navigation. Count keys are resolved live from case data, so a badge
   and the table it points at are always the same number.

   The entries carry catalogue keys rather than words. This file describes the
   shape of the cockpit — what is grouped with what, which badge counts what,
   which path is exact — and that shape is the same in every language. `SideNav`
   turns a key into a word at render, which is also what makes a language switch
   redraw the sidebar without this table being touched.

   The keys are namespaced because four of them are not the sidebar's to name.
   "MACD orders" is what that queue is called everywhere in the cockpit — in the
   routing tab, in Pixie's answers, in the CSV export — so the sidebar quotes
   `domain:queues.macd.label` rather than keeping a fifth copy that could drift
   from the other four. The rest are navigation's own wording and live in `nav`.

   Both key types are read from the English catalogue, so a renamed key fails the
   typecheck here rather than rendering `items.myQeueu` to an operator.

   Two entries from the original cockpit are deliberately absent: Orders, because
   the cockpit never creates a ProductOrder and so has no order register to show,
   and Conflicts, whose cases surface under Needs attention. */

export type CountKey = keyof NavCounters;

/** What a sidebar entry is called: either navigation's own wording, or the name
    a queue already carries in the domain vocabulary. */
export type NavLabelKey =
  | `nav:items.${keyof (typeof enNav)['items']}`
  | `domain:queues.${keyof (typeof enDomain)['queues']}.label`;

/** A leaf of `nav:groups` — the heading above a set of entries. */
export type NavGroupKey = keyof (typeof enNav)['groups'];

export interface NavItem {
  readonly labelKey: NavLabelKey;
  readonly path: string;
  readonly count?: CountKey;
  /** Marks a badge as something that should not be left sitting. */
  readonly tone?: 'alert';
  /** Match the path exactly rather than as a prefix. */
  readonly end?: boolean;
  /** Hidden unless the signed-in operator's session carries this capability.
   *
   *  Cosmetic, and deliberately so: the service refuses the reads behind these
   *  screens whatever the sidebar says, and the route still exists so a
   *  bookmarked link reports "you may not" rather than "no such page". What this
   *  removes is an entry that every operator can see and only some can use.
   *
   *  Absent on every other entry, which is the honest default — the rest of the
   *  cockpit is open to anybody who reached a tenant at all. */
  readonly capability?: CapabilityKey;
}

export type NavGroupIcon = 'queue' | 'doc' | 'branch' | 'catalog' | 'settings' | 'chart';

export interface NavGroup {
  readonly groupKey: NavGroupKey;
  readonly icon: NavGroupIcon;
  readonly items: readonly NavItem[];
}

export const navigation: readonly NavGroup[] = [
  {
    groupKey: 'work',
    icon: 'queue',
    items: [
      { labelKey: 'nav:items.overview', path: '/', end: true },
      { labelKey: 'nav:items.intake', path: '/intake', count: 'intake' },
      { labelKey: 'nav:items.myQueue', path: '/queue', count: 'mine', end: true },
      { labelKey: 'nav:items.teamQueue', path: '/queue/team', count: 'work' },

    ],
  },

  {
    groupKey: 'masterData',
    icon: 'catalog',
    items: [
      { labelKey: 'nav:items.serviceCatalog', path: '/catalog/service' },
      { labelKey: 'nav:items.customers', path: '/catalog/customers' },
      { labelKey: 'nav:items.requesterMasters', path: '/catalog/requesters' },
    ],
  },
  {
    groupKey: 'administration',
    icon: 'settings',
    items: [
      { labelKey: 'nav:items.mriCharacteristics', path: '/admin/mri' },
      { labelKey: 'nav:items.jobs', path: '/admin/jobs', capability: 'tenant:administer' },
      // { labelKey: 'nav:items.clarificationTemplates', path: '/admin/templates' },
      /* The two tenancy screens, last in the group because they are the ones
         that decide who reaches the other five. */
      {
        labelKey: 'nav:items.tenants',
        path: '/admin/tenants',
        capability: 'tenant:administer',
      },
      {
        labelKey: 'nav:items.tenantAccess',
        path: '/admin/access',
        capability: 'tenant:administer',
      },
      {
        labelKey: 'nav:items.tenantGroups',
        path: '/admin/tenant-groups',
        capability: 'tenantGroup:administer',
      },
      { labelKey: 'nav:items.settings', path: '/settings' },
    ],
  },
  {
    groupKey: 'insight',
    icon: 'chart',
    items: [
      // { labelKey: 'nav:items.analytics', path: '/analytics' },
      { labelKey: 'nav:items.reports', path: '/reports' },
    ],
  },
];
